import * as _ from 'lodash';
import React, { FormEvent, MouseEvent, useRef } from 'react';
import { IListItem, IRecipientFinancialInfo } from '@nkr/models/index';
import { InsuranceGroup } from '@nkr/enums/index';
import { Alert, Col, FormControl, FormGroup, Glyphicon, Row, Form, ControlLabel, Panel, Radio, InputGroup, HelpBlock } from 'react-bootstrap';
import { toast } from 'react-toastify';
import styled from 'styled-components';

const ContentWrapper = styled.div`
    margin: 10px;

    .has-error > .input-group > .bg-danger {
        color: white;
    }
`;

interface IProps {
    data: IRecipientFinancialInfo,
    allDetailsFilled: boolean,
    medicareDetailsNeeded: boolean,
    privateDetailsNeeded: boolean,
    isPrimaryInsuranceLocked: boolean,
    isSecondaryInsuranceLocked: boolean,
    privateDataCompleted: boolean,
    insuranceGroupList: IListItem[],
    onChange: (data: IRecipientFinancialInfo) => void
}

const Content = (props: IProps) => {
    const showErrorCooldownMilliseconds = 2000;
    const showNumericValueError = useRef(false);

    const handleDropDownChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let data = _.cloneDeep(props.data);

        let { name, value } = e.currentTarget;

        data[name] = parseInt(value);

        props.onChange(data);
    };

    const getSpecificsAddOn = (groupId: number, specifics: string) => {
        if (groupId == InsuranceGroup.Other && (specifics ?? '').trim() == '') {
            return (
                <span className='input-group-addon bg-danger'>
                    <Glyphicon glyph='remove' /> required
                </span>
            );
        } else if (groupId == InsuranceGroup.Other) {
            return (
                <span className='input-group-addon bg-success'>
                    <Glyphicon glyph='ok' /> required</span>
            );
        } else {
            return (
                <span className='input-group-addon'>optional</span>
            );
        }
    }

    const handleCheckButton = (e: MouseEvent<Radio & HTMLInputElement>) => {
        let data = _.cloneDeep(props.data);

        let { name, value } = e.currentTarget;

        if (parseInt(value) == 1) {
            data[name] = true;
        }

        if (parseInt(value) == 0) {
            data[name] = false;
        }

        if (parseInt(value) == -1) {
            data[name] = null;
        }

        props.onChange(data);
    };

    const handleText = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let data = _.cloneDeep(props.data);

        let { name, value } = e.currentTarget;

        data[name] = value;

        props.onChange(data);
    };

    const handlePrivInsGlobalEndDaysChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        const value = e.currentTarget.value;

        if (!Number.isFinite(Number(value)) || value.indexOf(' ') >= 0 || value == undefined) {
            e.preventDefault();

            // Show error message about only allowing numbers
            if (!showNumericValueError.current) {
                showNumericValueError.current = true;
                toast.POSITION.TOP_CENTER;
                toast.error('Only numerical values are allowed.');

                // Prevent the toast from being spammed on every stroke
                // set it so it only shows once every showErrorCooldownMilliseconds seconds.
                const timer = setTimeout(() => {
                    showNumericValueError.current = false;

                    clearTimeout(timer);
                }, showErrorCooldownMilliseconds);
            }

            return;
        } else if (value.length > 3) {
            e.preventDefault();
            return;
        }

        let newData = {
            ...data, privInsGlobalEndDays: value.toString()
        };

        props.onChange(newData);
    }

    const renderWarning = () => {
        return (
            <>
                {allDetailsFilled ?
                    <Panel.Body className='bg-success' style={{ paddingTop: 5, paddingBottom: 5 }}>
                        <div style={{ display: 'flow-root' }}>
                            <span style={{ float: 'left' }}>
                                <Glyphicon glyph='ok' />All fields required for DC are completed
                            </span>
                            <span style={{ float: 'right' }}>
                                <strong>Last Updated: </strong> {data.insuranceLastUpdated}
                            </span>
                        </div>
                    </Panel.Body>
                    :
                    <Panel.Body style={{ backgroundColor: '#fcf8e3', paddingTop: 5, paddingBottom: 5 }}>
                        Orange highlighted fields are required to record an acceptable DC
                    </Panel.Body>
                }
            </>
        );
    }

    if (props.data == null) {
        return <></>
    }

    let {
        insuranceGroupList, data, allDetailsFilled,
        medicareDetailsNeeded, privateDetailsNeeded,
        isPrimaryInsuranceLocked, isSecondaryInsuranceLocked
    } = props;

    let primaryOtherDetailsMissing = allDetailsFilled == false && data.primaryGroup == InsuranceGroup.Other && (data.primaryDetails ?? '').trim() == '';
    let secondaryOtherDetailsMissing = allDetailsFilled == false && data.secondaryGroup == InsuranceGroup.Other && (data.secondaryDetails ?? '').trim() == '';

    console.log(isPrimaryInsuranceLocked)

    return (
        <>
            <ContentWrapper>
                <Panel>
                    <Panel.Heading>Primary/Secondary Insurance</Panel.Heading>
                    <Panel.Body style={{ backgroundColor: '#fcf8e3', paddingTop: 5, paddingBottom: 5 }}>
                        If patient plans to enroll in Medicare at time of transplant, please select Medicare as secondary insurance.
                    </Panel.Body>
                    <Panel.Body>
                        <Row>
                            <Col xs={12} lg={12} style={{ margin: '5px' }}>
                                {(primaryOtherDetailsMissing || secondaryOtherDetailsMissing) && (
                                    <Alert bsStyle='danger' bsSize='sm'>
                                        {primaryOtherDetailsMissing && (
                                            <div>Please specify the details for the primary insurance...</div>
                                        )}

                                        {secondaryOtherDetailsMissing && (
                                            <div>Please specify the details for the secondary insurance...</div>
                                        )}
                                    </Alert>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} lg={3}></Col>
                            <Col xs={12} lg={3}>Insurance Group/Association</Col>
                            <Col xs={12} lg={3}>Specifics</Col>
                        </Row>

                        <Row>
                            <Col xs={12} lg={3} className='form-control-static'><span className='important-highlight'>Primary Insurance:</span></Col>
                            <Col xs={12} lg={3}>
                                <FormGroup>
                                    <div style={{ display: 'flex' }}>
                                        <FormControl
                                            componentClass='select'
                                            id='primaryGroup'
                                            name='primaryGroup'
                                            value={data.primaryGroup}
                                            disabled={isPrimaryInsuranceLocked}
                                            onChange={handleDropDownChange}
                                        >
                                            <option value={0}>- Select -</option>
                                            {insuranceGroupList.map((val) =>
                                                <option key={'pIG' + val.id} value={val.id}>{val.description}</option>
                                            )}
                                        </FormControl>

                                        {isPrimaryInsuranceLocked &&
                                            <span style={{ margin: '8px' }}>
                                                <Glyphicon glyph='lock' />
                                            </span>
                                        }
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col xs={12} lg={3}>
                                <FormGroup validationState={primaryOtherDetailsMissing ? 'error' : null}>
                                    <div className='input-group'>
                                        <FormControl
                                            id='primaryDetails'
                                            name='primaryDetails'
                                            value={data.primaryDetails}
                                            className='form-control'
                                            onChange={handleText}
                                            required={data.primaryGroup == 18}
                                        />
                                        {getSpecificsAddOn(data.primaryGroup, data.primaryDetails)}
                                    </div>

                                    {primaryOtherDetailsMissing &&
                                        <HelpBlock>
                                            This is required when 'Other' is selected...
                                        </HelpBlock>
                                    }
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} lg={3} className='form-control-static'>Secondary Insurance</Col>
                            <Col xs={12} lg={3}>
                                <FormGroup>
                                    <div style={{ display: 'flex' }}>
                                        <FormControl
                                            componentClass='select'
                                            id='secondaryGroup'
                                            name='secondaryGroup'
                                            value={data.secondaryGroup}
                                            disabled={isSecondaryInsuranceLocked}
                                            onChange={handleDropDownChange}
                                        >
                                            <option value={0}>- N/A -</option>
                                            {insuranceGroupList.map((val) =>
                                                <option key={'sIG' + val.id} value={val.id}>{val.description}</option>
                                            )}
                                        </FormControl>

                                        {isSecondaryInsuranceLocked &&
                                            <span style={{ margin: '8px' }}>
                                                <Glyphicon glyph='lock' />
                                            </span>
                                        }
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col xs={12} lg={3}>
                                <FormGroup validationState={secondaryOtherDetailsMissing ? 'error' : null}>
                                    <div className='input-group'>
                                        <FormControl
                                            id='secondaryDetails'
                                            name='secondaryDetails'
                                            value={data.secondaryDetails}
                                            className='form-control'
                                            onChange={handleText}
                                            required={data.secondaryGroup == 18}
                                        />
                                        {getSpecificsAddOn(data.secondaryGroup, data.secondaryDetails)}
                                    </div>

                                    {secondaryOtherDetailsMissing &&
                                        <HelpBlock>
                                            This is required when 'Other' is selected...
                                        </HelpBlock>
                                    }
                                </FormGroup>
                            </Col>
                        </Row>
                    </Panel.Body>
                </Panel>

                {medicareDetailsNeeded &&
                    <Panel>
                        <Panel.Heading>Medicare Details</Panel.Heading>
                        {renderWarning()}
                        <Panel.Body>
                            <Form horizontal>
                                <FormGroup>
                                    <Col xs={12} lg={2} componentClass={ControlLabel}> <span className='important-highlight'>Patient's Medicare: </span></Col>
                                    <Col xs={12} lg={9}>
                                        <Radio
                                            inline
                                            id='isMedicarePostTransplant0'
                                            name='isMedicarePostTransplant'
                                            value={0}
                                            checked={data.isMedicarePostTransplant == false}
                                            onClick={handleCheckButton}>Is Currently Active</Radio>

                                        <Radio
                                            inline
                                            id='isMedicarePostTransplant1'
                                            name='isMedicarePostTransplant'
                                            value={1}
                                            checked={data.isMedicarePostTransplant == true}
                                            onClick={handleCheckButton}>Will be applied for at time of transplant</Radio>
                                    </Col>
                                </FormGroup>
                                <FormGroup>
                                    <Col xs={12} lg={2} componentClass={ControlLabel}>Medicare Number:</Col>
                                    <Col xs={12} lg={9}>
                                        <FormControl
                                            id='medicareNumber'
                                            name='medicareNumber'
                                            value={data.medicareNumber}
                                            onChange={handleText}
                                            maxLength={32}
                                            className='form-control'
                                        />
                                    </Col>
                                </FormGroup>
                            </Form>
                        </Panel.Body>
                    </Panel>
                }

                {privateDetailsNeeded &&
                    <Panel>
                        <Panel.Heading>Private Insurance Details</Panel.Heading>
                        {renderWarning()}
                        <Panel.Body>
                            <Row>
                                <Col xs={12} lg={12} style={{ margin: '5px' }}>
                                    {!props.privateDataCompleted && (
                                        <Alert bsStyle='danger' bsSize='sm'>
                                            <div>Please specify the <span className='important-highlight'>highlighted</span> fields below...</div>
                                        </Alert>
                                    )}
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12} lg={6}>
                                    <Form horizontal>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className='important-highlight'>Global Case Rate:</span></Col>
                                            <Col xs={12} lg={5}>
                                                <Radio
                                                    inline
                                                    id='globalCaseRate1'
                                                    name='globalCaseRate'
                                                    value={1}
                                                    checked={data.globalCaseRate == true}
                                                    onClick={handleCheckButton}>Yes</Radio>

                                                <Radio
                                                    inline
                                                    id='globalCaseRate0'
                                                    name='globalCaseRate'
                                                    value={0}
                                                    checked={data.globalCaseRate == false}
                                                    onClick={handleCheckButton}>No</Radio>
                                            </Col>
                                        </FormGroup>
                                        {data.globalCaseRate &&
                                            <FormGroup validationState={props.data.privInsGlobalEndDays === null ? 'error' : null}>
                                                <Col xs={12} lg={5} componentClass={ControlLabel}>
                                                    <span className={data.globalCaseRate ? 'important-highlight' : null}>Global Period Length After Discharge Date:</span>
                                                </Col>
                                                <Col xs={12} lg={5}>
                                                    <InputGroup>
                                                        <FormControl
                                                            id='globalEndDays'
                                                            name='globalEndDays'
                                                            value={props.data.privInsGlobalEndDays || ''}
                                                            className='form-control'
                                                            onChange={handlePrivInsGlobalEndDaysChange}
                                                        />
                                                        <InputGroup.Addon>Days</InputGroup.Addon>
                                                    </InputGroup>
                                                </Col>
                                            </FormGroup>
                                        }
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>Professional Case Rate:</Col>
                                            <Col xs={12} lg={5}>
                                                <Radio
                                                    inline
                                                    id='professionalCaseRate1'
                                                    name='professionalCaseRate'
                                                    value={1}
                                                    checked={data.professionalCaseRate == true}
                                                    onClick={handleCheckButton}>Yes</Radio>

                                                <Radio
                                                    inline
                                                    id='professionalCaseRate1'
                                                    name='professionalCaseRate'
                                                    value={0}
                                                    checked={data.professionalCaseRate == false}
                                                    onClick={handleCheckButton}>No</Radio>

                                                <Radio
                                                    inline
                                                    id='professionalCaseRateUnknown'
                                                    name='professionalCaseRate'
                                                    value={-1}
                                                    checked={data.professionalCaseRate == null}
                                                    onClick={handleCheckButton}>Unknown</Radio>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>Subscribers Relationship to Patient:</Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='relationshipToSubscriber'
                                                    name='relationshipToSubscriber'
                                                    value={data.relationshipToSubscriber}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Patient's Name: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='patientName'
                                                    name='patientName'
                                                    value={data.patientName}
                                                    maxLength={32}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}> <span className={data.globalCaseRate == false ? 'important-highlight' : null}>Policy ID #: </span> </Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='policyIdNumber'
                                                    name='policyIdNumber'
                                                    value={data.policyIdNumber}
                                                    maxLength={32}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Group #: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='groupNumber'
                                                    name='groupNumber'
                                                    value={data.groupNumber}
                                                    maxLength={32}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>Benefit Phone:</Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='benefitPhone'
                                                    name='benefitPhone'
                                                    value={data.benefitPhone}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>Precert Phone:</Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='precertificationPhone'
                                                    name='precertificationPhone'
                                                    value={data.precertificationPhone}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                    </Form>
                                </Col>
                                <Col xs={12} lg={6} style={{ marginTop: '75px' }}>
                                    <Form horizontal>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Health Plan Name: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='healthPlanName'
                                                    name='healthPlanName'
                                                    value={data.healthPlanName}
                                                    maxLength={32}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Claims Mailing Address: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='claimsMailingAddress'
                                                    name='claimsMailingAddress'
                                                    value={data.claimsMailingAddress}
                                                    className='form-control'
                                                    componentClass='textarea'
                                                    cols={8}
                                                    rows={4}
                                                    style={{ height: 90, resize: 'none' }}
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Subscriber Name: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='subscriberName'
                                                    name='subscriberName'
                                                    value={data.subscriberName}
                                                    maxLength={32}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Case Manager Name: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='caseManagerName'
                                                    name='caseManagerName'
                                                    value={data.caseManagerName}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}><span className={data.globalCaseRate == false ? 'important-highlight' : null}>Case Manager Phone: </span></Col>
                                            <Col xs={12} lg={5}>
                                                <FormControl
                                                    id='caseManagerPhone'
                                                    name='caseManagerPhone'
                                                    value={data.caseManagerPhone}
                                                    className='form-control'
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                    </Form>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={12}>
                                    <Form horizontal>
                                        <FormGroup>
                                            <Col xs={12} lg={12} componentClass={ControlLabel}>Financial Comments</Col>
                                            <Col xs={12} lg={11}>
                                                <FormControl
                                                    id='financialComments'
                                                    name='financialComments'
                                                    value={data.financialComments}
                                                    componentClass='textarea'
                                                    style={{ height: 170, resize: 'none' }}
                                                    onChange={handleText}
                                                />
                                            </Col>
                                        </FormGroup>
                                    </Form>
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                }
            </ContentWrapper>
        </>
    );
}


export default Content;
